import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import PageHeader from '../shared/PageHeader';
import AccountDashboard from './AccountDashboard';
import { AgencyLogo, useAgencyLogos } from './AgencyLogo';
import '../shared/shared-chrome.css';
import './Accounts.css';

/*
 * AccountsPage — /accounts, the TekStak mirror of the GAS app's
 * Customers section (review doc §10, P5a). Same contract as
 * AccountWorkspace: selectedDepartment / departments /
 * onDepartmentSelect come from MainApp.
 *
 * No department selected → portfolio intro: search picker over all
 * departments + a card grid of the user's first saved account list
 * (same /api/account-lists store AccountListsPage manages).
 * Department selected → per-account dashboard with the GAS tab strip.
 */

function PortfolioIntro({ departments, onDepartmentSelect }) {
  const [query, setQuery] = useState('');

  // First saved account list → "My accounts" card grid (optional; the
  // page works without one).
  const lists = useQuery({
    queryKey: ['account-lists'],
    queryFn: () => apiClient.get('/api/account-lists'),
  });
  const firstListId = lists.data?.[0]?.id;
  const listDetail = useQuery({
    queryKey: ['account-list', firstListId],
    queryFn: () => apiClient.get(`/api/account-lists/${firstListId}`),
    enabled: !!firstListId,
  });

  const members = (listDetail.data?.members || []).filter((m) => m.department_id);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return (departments || [])
      .filter((d) =>
        (d.official_name || '').toLowerCase().includes(q) ||
        (d.common_name || '').toLowerCase().includes(q) ||
        (d.acronym || '').toLowerCase().includes(q)
      )
      .slice(0, 12);
  }, [departments, query]);

  // Logos for whichever grid is showing (search results or My accounts).
  const visibleIds = (query.trim() ? results : members).map((x) => x.department_id);
  const logoMap = useAgencyLogos(visibleIds);

  return (
    <div className="accounts-portfolio">
      <PageHeader
        title="Accounts"
        blurb="Pick an agency to open its account dashboard — spend, org chart, notes, and zero trust in one place."
      />

      <div className="accounts-search">
        <Search size={16} className="accounts-search-icon" />
        <input
          type="search"
          className="accounts-search-input"
          placeholder="Search agencies by name or acronym…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search agencies"
        />
      </div>

      {query.trim() && (
        <div className="accounts-grid">
          {results.length === 0 && (
            <p className="accounts-panel-note">No agencies match "{query}".</p>
          )}
          {results.map((d) => (
            <button
              key={d.department_id}
              className="ts-card ts-card--hover accounts-dept-card"
              onClick={() => onDepartmentSelect(d.department_id)}
            >
              <AgencyLogo
                url={logoMap[d.department_id]}
                acronym={d.acronym}
                badgeClass="accounts-dept-acronym"
                logoClass="accounts-dept-logo"
              />
              <span className="accounts-dept-name">{d.official_name || d.common_name}</span>
            </button>
          ))}
        </div>
      )}

      {!query.trim() && (
        <>
          <div className="ts-section-label accounts-section-label">
            {listDetail.data?.name ? `My accounts — ${listDetail.data.name}` : 'My accounts'}
          </div>
          {(lists.isLoading || listDetail.isLoading) && (
            <p className="accounts-panel-note">Loading your account list…</p>
          )}
          {(lists.error || listDetail.error) && (
            <p className="accounts-panel-note">
              Couldn't load your account list — use the search above instead.
            </p>
          )}
          {!lists.isLoading && !lists.error && !firstListId && (
            <p className="accounts-panel-note">
              No saved account list yet — search above, or build one under Account lists.
            </p>
          )}
          {members.length > 0 && (
            <div className="accounts-grid">
              {members.map((m) => (
                <button
                  key={m.id}
                  className="ts-card ts-card--hover accounts-dept-card"
                  onClick={() => onDepartmentSelect(m.department_id)}
                >
                  <AgencyLogo
                    url={logoMap[m.department_id]}
                    acronym={m.acronym}
                    badgeClass="accounts-dept-acronym"
                    logoClass="accounts-dept-logo"
                  />
                  <span className="accounts-dept-name">
                    {m.official_name || m.common_name || m.salesforce_account_name}
                  </span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function AccountsPage({ selectedDepartment, departments, onDepartmentSelect }) {
  if (!selectedDepartment) {
    return (
      <div className="accounts-page">
        <PortfolioIntro departments={departments} onDepartmentSelect={onDepartmentSelect} />
      </div>
    );
  }
  return (
    <div className="accounts-page">
      <AccountDashboard
        selectedDepartment={selectedDepartment}
        departments={departments}
      />
    </div>
  );
}
